import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Divider, ListItemIcon, ListItemText } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import SensorsIcon from '@mui/icons-material/Sensors';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from 'constants/RoutePaths';
import { useAuthContext } from 'hooks/useAuthContext';
import { useLogout } from 'hooks/useLogout';
import WorkspaceDropdown from 'components/WorkspaceDropdown/WorkspaceDropdown';
import { EmptyUserAvatar } from 'components/EmptyUserAvatar/EmptyUserAvatar';
import { NAV_ITEMS, isNavItemVisible, isNavItemActive } from './navItems';
import {
  StyledDrawer,
  StyledHeaderStack,
  StyledWorkspaceStack,
  StyledUserName,
  StyledList,
  StyledListItemButton,
  LogoutListItemText,
  StyledUserAvatar,
  StyledAvatarBox,
} from './MobileNavDrawer.styles';

const NAV_ICONS: Record<string, JSX.Element> = {
  home: <HomeIcon />,
  profile: <PersonIcon />,
  trainings: <SchoolIcon />,
  users: <GroupIcon />,
  live: <SensorsIcon />,
  statistics: <BarChartIcon />,
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export const MobileNavDrawer = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userState } = useAuthContext();
  const logout = useLogout();

  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const userRole = userState.user.selectedWorkspace.userRole;
  const visibleItems = NAV_ITEMS.filter((item) => isNavItemVisible(item, userRole));

  const handleLogoutClick = async () => {
    onClose();
    await logout();
  };

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <StyledHeaderStack direction="row" spacing={2} alignItems="center">
        <StyledAvatarBox>
          {userState.user.selectedWorkspace.userPhotoUrl ? (
            <StyledUserAvatar src={userState.user.selectedWorkspace.userPhotoUrl} alt="User Avatar" />
          ) : (
            <EmptyUserAvatar color="black" fontSize="25px" padding="0px" />
          )}
        </StyledAvatarBox>
        <StyledUserName variant="subtitle1">
          {userState.user.selectedWorkspace.userDisplayName}
        </StyledUserName>
      </StyledHeaderStack>

      <StyledWorkspaceStack>
        <WorkspaceDropdown />
      </StyledWorkspaceStack>

      <Divider />

      <StyledList>
        {visibleItems.map((item) => (
          <StyledListItemButton
            key={item.id}
            component={RouterLink}
            to={item.path}
            selected={isNavItemActive(item, location.pathname)}
            data-testid={`mobile-nav-drawer-${item.id}`}
          >
            <ListItemIcon>{NAV_ICONS[item.id]}</ListItemIcon>
            <ListItemText primary={t(item.labelKey)} />
          </StyledListItemButton>
        ))}
      </StyledList>

      <Divider />

      <StyledList>
        <StyledListItemButton onClick={handleLogoutClick} data-testid="mobile-nav-drawer-logout">
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <LogoutListItemText primary={t('navbar.logout')} />
        </StyledListItemButton>
      </StyledList>
    </StyledDrawer>
  );
};